import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { OnboardingEdition } from '@/types';
import { GithubGlyph, githubUsername } from './marks';
import { OnboardingShell, titleFont } from './OnboardingShell';
import { TermsStep } from './TermsStep';

// _components/welcome-and-terms.tsx. Only the legacy 25 flow shows the GitHub welcome
// card before the bases; the three 26 editions render the terms straight away, which is
// what `edition.githubWelcome` decides (PORTING.md G5).
export function WelcomeAndTerms({
  edition,
  hackerGithub,
  userName,
  userEmail,
  userImage,
}: {
  edition: OnboardingEdition;
  hackerGithub: string | null;
  userName: string;
  userEmail: string;
  userImage: string | null;
}) {
  const [showTerms, setShowTerms] = useState(false);

  if (!edition.githubWelcome || showTerms) {
    return <TermsStep edition={edition} />;
  }

  const username = githubUsername(hackerGithub);
  const githubProfileUrl = `https://github.com/${username}`;
  const githubAvatarUrl = userImage || `https://github.com/${username}.png`;

  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-4xl">
      {/* Title */}
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          ¡Bienvenido{' '}
          <span className="bg-primary px-2 py-1 text-background">
            {userName.split(' ')[0]}!
          </span>
        </h1>
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Tu cuenta de GitHub ha sido conectada exitosamente
        </p>
      </div>

      {/* Success Card */}
      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
        <div className="space-y-6">
          {/* Success Alert */}
          <Alert className="border-green-500 bg-green-50">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <AlertTitle className="text-green-800">
              ¡Cuenta conectada exitosamente!
            </AlertTitle>
            <AlertDescription className="text-green-700">
              Tu cuenta de GitHub se ha vinculado correctamente.
            </AlertDescription>
          </Alert>

          {/* Profile Info */}
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <img
                src={githubAvatarUrl}
                alt={`${username} avatar`}
                width={120}
                height={120}
                className="rounded-full border-4 border-green-500"
              />
              <div className="absolute right-0 bottom-0 rounded-full bg-green-500 p-2">
                <GithubGlyph className="h-6 w-6 text-white" />
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 text-center">
              <h2
                className={`font-bold ${titleFont(edition.ph26)} text-3xl text-primary`}
              >
                {userName}
              </h2>
              <p className="font-mono text-primary/70 text-sm">{userEmail}</p>
            </div>
          </div>

          {/* GitHub Info */}
          <div className="border-primary/20 border-t pt-6">
            <div className="bg-green-50 p-4">
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`font-bold ${titleFont(edition.ph26)} text-green-800`}
                >
                  Usuario de GitHub:
                </span>
                <a
                  href={githubProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 font-mono text-green-700 transition-colors hover:underline"
                >
                  @{username}
                </a>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="border-primary/20 border-t pt-6">
            <h3
              className={`mb-4 font-bold ${titleFont(edition.ph26)} text-primary text-xl`}
            >
              Siguiente paso
            </h3>
            <p className="mb-4 font-mono text-primary/80 text-sm">
              Para continuar con el onboarding, necesitas aceptar las bases del
              hackathon.
            </p>
          </div>

          {/* Continue Button */}
          <div className="flex justify-center pt-4">
            <Button
              size="lg"
              className="w-full sm:w-auto"
              onClick={() => setShowTerms(true)}
            >
              Continuar al siguiente paso
            </Button>
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
}
